import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "../lib/api";
import { PROVINCES, VEH_TYPES, LIC_CATEGORIES, ORG_TYPES, PERSON_STATUTS, VEHICLE_STATUTS, LICENSE_STATUTS, ORG_TYPE_LABELS } from "../lib/constants";

function Field({ label, children, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Sel({ value, onChange, options, labels, placeholder = "Selectionner", testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{labels ? labels[o] || o : o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function OrgSelect({ value, onChange, testid }) {
  const [orgs, setOrgs] = useState([]);
  useEffect(() => {
    API.get("/organizations", { params: { page_size: 100 } })
      .then((r) => setOrgs(r.data.items))
      .catch(() => {});
  }, []);
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? null : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Aucune" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Aucune</SelectItem>
        {orgs.map((o) => (
          <SelectItem key={o.id} value={o.id}>{o.nom}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function PersonForm({ initial = {}, onChange }) {
  const v = { ...initial };
  const set = (k) => (e) => onChange({ ...v, [k]: e?.target ? e.target.value : e });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Nom *"><Input value={v.nom || ""} onChange={set("nom")} data-testid="person-form-nom" /></Field>
      <Field label="Prenom *"><Input value={v.prenom || ""} onChange={set("prenom")} data-testid="person-form-prenom" /></Field>
      <Field label="Date de naissance"><Input type="date" value={v.date_naissance || ""} onChange={set("date_naissance")} data-testid="person-form-dob" /></Field>
      <Field label="Lieu de naissance"><Input value={v.lieu_naissance || ""} onChange={set("lieu_naissance")} /></Field>
      <Field label="Sexe"><Sel value={v.sexe} onChange={set("sexe")} options={["M", "F"]} testid="person-form-sexe" /></Field>
      <Field label="Nationalite"><Input value={v.nationalite || "Gabonaise"} onChange={set("nationalite")} /></Field>
      <Field label="Telephone"><Input value={v.telephone || ""} onChange={set("telephone")} data-testid="person-form-telephone" /></Field>
      <Field label="Email"><Input type="email" value={v.email || ""} onChange={set("email")} /></Field>
      <Field label="Adresse"><Input value={v.adresse || ""} onChange={set("adresse")} /></Field>
      <Field label="Ville"><Input value={v.ville || ""} onChange={set("ville")} data-testid="person-form-ville" /></Field>
      <Field label="Province"><Sel value={v.province} onChange={set("province")} options={PROVINCES} testid="person-form-province" /></Field>
      <Field label="Profession"><Input value={v.profession || ""} onChange={set("profession")} /></Field>
      <Field label="Fonction"><Input value={v.fonction || ""} onChange={set("fonction")} /></Field>
      <Field label="Structure"><OrgSelect value={v.organization_id} onChange={set("organization_id")} testid="person-form-structure" /></Field>
      <Field label="Matricule"><Input value={v.matricule || ""} onChange={set("matricule")} /></Field>
      <Field label="Statut"><Sel value={v.statut || "ACTIF"} onChange={set("statut")} options={PERSON_STATUTS} testid="person-form-statut" /></Field>
    </div>
  );
}

export function VehicleForm({ initial = {}, onChange }) {
  const v = { ...initial };
  const set = (k) => (e) => onChange({ ...v, [k]: e?.target ? e.target.value : e });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Immatriculation *"><Input value={v.immatriculation || ""} onChange={set("immatriculation")} placeholder="GA-123-AA" className="mono" data-testid="vehicle-form-immat" /></Field>
      <Field label="VIN / Chassis"><Input value={v.vin || ""} onChange={set("vin")} className="mono" data-testid="vehicle-form-vin" /></Field>
      <Field label="Marque"><Input value={v.marque || ""} onChange={set("marque")} data-testid="vehicle-form-marque" /></Field>
      <Field label="Modele"><Input value={v.modele || ""} onChange={set("modele")} /></Field>
      <Field label="Type"><Sel value={v.type} onChange={set("type")} options={VEH_TYPES} testid="vehicle-form-type" /></Field>
      <Field label="Couleur"><Input value={v.couleur || ""} onChange={set("couleur")} /></Field>
      <Field label="Annee"><Input type="number" value={v.annee || ""} onChange={set("annee")} data-testid="vehicle-form-annee" /></Field>
      <Field label="Mise en circulation"><Input type="date" value={v.date_mec || ""} onChange={set("date_mec")} /></Field>
      <Field label="Proprietaire"><Input value={v.proprietaire_nom || ""} onChange={set("proprietaire_nom")} /></Field>
      <Field label="Structure"><OrgSelect value={v.organization_id} onChange={set("organization_id")} testid="vehicle-form-structure" /></Field>
      <Field label="Statut"><Sel value={v.statut || "EN_CIRCULATION"} onChange={set("statut")} options={VEHICLE_STATUTS} testid="vehicle-form-statut" /></Field>
    </div>
  );
}

export function OrgForm({ initial = {}, onChange }) {
  const v = { ...initial };
  const set = (k) => (e) => onChange({ ...v, [k]: e?.target ? e.target.value : e });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Nom *"><Input value={v.nom || ""} onChange={set("nom")} data-testid="org-form-nom" /></Field>
      <Field label="Sigle"><Input value={v.sigle || ""} onChange={set("sigle")} /></Field>
      <Field label="Type"><Sel value={v.type || "AUTRE"} onChange={set("type")} options={ORG_TYPES} labels={ORG_TYPE_LABELS} testid="org-form-type" /></Field>
      <Field label="Adresse"><Input value={v.adresse || ""} onChange={set("adresse")} /></Field>
      <Field label="Province"><Sel value={v.province} onChange={set("province")} options={PROVINCES} testid="org-form-province" /></Field>
      <Field label="Ville"><Input value={v.ville || ""} onChange={set("ville")} /></Field>
      <Field label="Telephone"><Input value={v.telephone || ""} onChange={set("telephone")} /></Field>
      <Field label="Email"><Input type="email" value={v.email || ""} onChange={set("email")} /></Field>
      <Field label="Responsable"><Input value={v.responsable || ""} onChange={set("responsable")} /></Field>
      <Field label="Statut"><Sel value={v.statut || "ACTIF"} onChange={set("statut")} options={PERSON_STATUTS} testid="org-form-statut" /></Field>
    </div>
  );
}

export function LicenseForm({ initial = {}, onChange, hideStatut }) {
  const v = { ...initial };
  const set = (k) => (e) => onChange({ ...v, [k]: e?.target ? e.target.value : e });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Numero *"><Input value={v.numero || ""} onChange={set("numero")} className="mono" data-testid="license-form-numero" /></Field>
      <Field label="Categorie"><Sel value={v.categorie || "B"} onChange={set("categorie")} options={LIC_CATEGORIES} testid="license-form-categorie" /></Field>
      <Field label="Date de delivrance"><Input type="date" value={v.date_delivrance || ""} onChange={set("date_delivrance")} /></Field>
      <Field label="Date d'expiration"><Input type="date" value={v.date_expiration || ""} onChange={set("date_expiration")} /></Field>
      {!hideStatut && <Field label="Statut"><Sel value={v.statut || "VALIDE"} onChange={set("statut")} options={LICENSE_STATUTS} testid="license-form-statut" /></Field>}
    </div>
  );
}
